import UserContext from '@/context/user-context'
import { IUser } from '@giphy/js-types'
import { useRouter } from 'next/navigation'
import { ReactNode, useContext } from 'react'
import { ILoggedInUser } from 'types'
import { AltDropdown } from './alt-dropdown'
import ChannelAccessFooter from './channel-access-footer'
import { MenuContext, MenuProvider } from './context/menu-context'
import { DropDownContainer } from './dropdown'
import {
    AdvertiseIcon,
    CaretIcon,
    CategoriesIcon,
    CreateIcon,
    FavoritesIcon,
    LoginIcon,
    MoreIcon,
    ReactionsIcon,
} from './icons'
import { categories } from './lists/categories'
import { moreList } from './lists/more'
import { reactions } from './lists/reactions'
import AdvertiseContent from './menuContents/advertise'
import ChannelsContent from './menuContents/channel'
import CreateContent from './menuContents/create'
import MoreMenu from './menuContents/more'
import DropdownContent from './menuContents/reactions'

export type MenuKey = 'reactions' | 'favorites' | 'categories' | 'create' | 'advertise' | 'login' | 'mychannel' | 'more'
type MenuItem = {
    label: string
    icon: ReactNode
    hasCaret: boolean
    gradient?: string
    component?: ReactNode
    onClick?: () => void
}

export type AccessProps = {
    user: IUser & {
        is_staff?: boolean
        channel_access?: {
            access: Access[]
            emulated_user?: IUser
            user?: IUser
        }
    }
}

export type Access = {
    channel: {
        avatar_url: string
        user_id: number
        display_name: string
        id: number
        slug: string
        url: string
    }
    create_datetime: string
    dashboard_url: string
    id: number
    last_login_datetime: string
    login_url: string
    permission_level: number
    update_datetime: string
}

const isLoggedInUser = (user: any): user is ILoggedInUser => {
    return user && typeof user.is_staff !== 'undefined'
}

const NavigationLayout = () => {
    const { user } = useContext(UserContext)
    const router = useRouter()

    const { activeMenu, caretPosition, caretRefs, handleMouseEnter, handleMouseLeave, navRef } = useContext(MenuContext)

    const menuItems: Record<MenuKey, MenuItem> = {
        reactions: {
            label: 'Reactions',
            icon: <ReactionsIcon />,
            hasCaret: true,
            gradient: 'linear-gradient(315deg, #0CF 0%, #5D5FEF 100%)',
            component: <DropdownContent title={'Reactions'} items={reactions} />,
            onClick: () => router.push('/reactions'),
        },
        favorites: {
            label: 'Favorites',
            icon: <FavoritesIcon />,
            hasCaret: false,
            gradient: '',
            onClick: () => router.push('/favorites'),
        },
        categories: {
            label: 'Categories',
            icon: <CategoriesIcon />,
            hasCaret: true,
            gradient: 'linear-gradient(240deg, #F66 7.79%, #E646B6 92.21%)',
            component: <DropdownContent title="Categories" items={categories} />,
            onClick: () => router.push('/categories'),
        },
        create: {
            label: 'Create',
            icon: <CreateIcon />,
            hasCaret: false,
            gradient: 'linear-gradient(180deg, #2E2E2E 0%, #171717 100%)',
            component: <CreateContent />,
            onClick: () => router.push('/create'),
        },
        advertise: {
            label: 'Advertise',
            icon: <AdvertiseIcon />,
            hasCaret: false,
            gradient: 'linear-gradient(180deg, #2E2E2E 0%, #171717 100%)',
            component: <AdvertiseContent />,
            onClick: () => router.push('http://ads.giphy.com'),
        },
        login: {
            label: 'Log In',
            icon: <LoginIcon />,
            hasCaret: false,
            onClick: () => router.push('/login'),
        },
        mychannel: {
            label: 'My Channel',
            icon: <LoginIcon />,
            hasCaret: true,
            gradient: 'linear-gradient(180deg, #2E2E2E 0%, #171717 100%)',
            component: user ? <ChannelsContent user={user} /> : undefined,
            onClick: () => router.push('/login'),
        },
        more: {
            label: 'More',
            icon: <MoreIcon />,
            hasCaret: false,
            gradient: 'linear-gradient(233deg, #93F 0%, #6157FF 100%)',
            component: <MoreMenu data={moreList.map((section) => ({ ...section, items: [...section.items] }))} />,
        },
    } as const

    const menuOrder: MenuKey[] = isLoggedInUser(user)
        ? ['reactions', 'favorites', 'create', 'advertise', 'mychannel', 'more']
        : ['reactions', 'categories', 'create', 'advertise', 'login', 'more']

    const activeMenuItem = activeMenu ? menuItems[activeMenu as MenuKey] : undefined

    return (
        <>
            <nav
                ref={navRef}
                className="relative z-header mb-2 flex h-14 w-full items-end justify-end bg-giphyBlack text-white md:flex"
                onMouseLeave={handleMouseLeave}
            >
                <div className="flex items-center justify-center gap-3">
                    {menuOrder.map((key) => {
                        const item = menuItems[key]
                        return (
                            <div
                                key={key}
                                className="flex cursor-pointer items-center gap-1 rounded-md px-2 py-0 transition-colors"
                                onMouseEnter={() => handleMouseEnter(key)}
                                onMouseLeave={() => handleMouseLeave()}
                                onClick={item.onClick}
                                style={!!activeMenu && activeMenu !== key ? { opacity: 0.75, transition: '.25s' } : {}}
                            >
                                {item.hasCaret ? (
                                    <>
                                        {' '}
                                        {activeMenu === key ? (
                                            <div className="animate-scale-bounce">{item.icon}</div>
                                        ) : (
                                            <span className="flex items-center ">{item.icon}</span>
                                        )}
                                        <span className="text-[17px] font-bold tracking-wide">{item.label}</span>
                                        <span
                                            ref={(el) => {
                                                caretRefs.current[key] = el
                                            }}
                                        >
                                            <CaretIcon rotated={activeMenu === key} />
                                        </span>
                                    </>
                                ) : (
                                    <span className="flex items-center">
                                        <span
                                            className={
                                                'flex items-center' +
                                                (item.label === 'Log In' || item.label === 'My Channel'
                                                    ? ' mr-0'
                                                    : item.label === 'More'
                                                      ? ' mr-3'
                                                      : ' mr-1')
                                            }
                                        >
                                            {activeMenu === key && item.label !== 'More' ? (
                                                <div className="animate-scale-bounce">{item.icon}</div>
                                            ) : (
                                                <span>{item.icon}</span>
                                            )}
                                        </span>
                                        <span
                                            ref={(el) => {
                                                caretRefs.current[key] = el
                                            }}
                                            className="text-[17px] font-bold tracking-wide"
                                        >
                                            {item.label}
                                        </span>
                                    </span>
                                )}
                            </div>
                        )
                    })}
                </div>
                {activeMenuItem?.component &&
                    activeMenu &&
                    (['reactions', 'categories', 'more'].includes(activeMenu) ? (
                        <DropDownContainer
                            menuItemKey={activeMenu}
                            caretPosition={caretPosition ?? 0}
                            gradient={activeMenuItem.gradient}
                            title={activeMenuItem.label}
                        >
                            {activeMenuItem.component}
                        </DropDownContainer>
                    ) : ['create', 'advertise', 'mychannel'].includes(activeMenu) ? (
                        <AltDropdown
                            menuItemKey={activeMenu}
                            label={activeMenu as string}
                            caretPosition={caretPosition}
                            gradient={activeMenuItem.gradient}
                        >
                            {activeMenuItem.component}
                        </AltDropdown>
                    ) : null)}
            </nav>
            {user && <ChannelAccessFooter user={user as AccessProps['user']} />}
        </>
    )
}

export const DesktopNav = () => (
    <MenuProvider>
        <NavigationLayout />
    </MenuProvider>
)
